// "Database access (SQL clients)" on the project page of a HOSTED project: the signed-in user
// creates a personal PostgreSQL login for R, Python, DBeaver or a BI tool. The password is shown
// ONCE, in the answer of POST /api/projects/{id}/sql-logins, and kept only in this component's
// state until the panel is left. The login gets the user's data role (reader / editor / admin;
// the backend keeps it in step). A data admin also sees and revokes everybody's logins.
import { useState } from "react";
import { api } from "../api";
import { ErrorNote, Loading, SectionTitle } from "../components/Feedback";
import { Badge, Btn, Card, Field, Input } from "../components/Shared";
import { useApi } from "../useApi";

const MONO = { fontFamily: "'JetBrains Mono', monospace" };

function when(iso) {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

function Snippet({ label, children }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 12, color: "#4b5563" }}>{label}</div>
      <pre style={{ ...MONO, margin: "4px 0 0", padding: "8px 10px", background: "#f3f4f6", borderRadius: 6,
                    fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{children}</pre>
    </div>
  );
}

export default function SqlLoginsPanel({ projectId }) {
  const state = useApi(`/projects/${projectId}/sql-logins`);
  const [days, setDays] = useState("");
  const [created, setCreated] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const d = state.data;

  const create = async e => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body = days ? { valid_days: Number(days) } : {};
      setCreated(await api.post(`/projects/${projectId}/sql-logins`, body));
      state.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async login => {
    setError(null);
    setBusy(true);
    try {
      await api.del(`/projects/${projectId}/sql-logins/${login.id}`);
      if (created && created.login.id === login.id) setCreated(null);
      state.reload();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const c = d?.connection;
  return (
    <section aria-label="SQL clients" style={{ marginBottom: 24 }}>
      <SectionTitle>Database access for SQL clients</SectionTitle>
      <ErrorNote error={state.error || error} />
      {state.loading && !d && <Loading />}
      {d && (
        <Card style={{ padding: "18px 22px" }}>
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "#374151" }}>
            A personal login connects R, Python, DBeaver or a BI tool straight to <code>{c.database}</code> on{" "}
            <code>{c.host}:{c.port}</code>, with your data role (<Badge color="blue">{d.my_level}</Badge>). The
            password is shown once; creating the login again gives it a new one. It stops working when it
            expires, when you are revoked, lose your role, or the database stops being hosted. At most{" "}
            {d.connection_limit} connections at a time.
          </p>
          {d.mine ? (
            <p style={{ margin: "0 0 12px", fontSize: 14 }}>
              Your login: <code style={MONO}>{d.mine.role_name}</code> ({d.mine.level}), valid until{" "}
              {when(d.mine.expires_at)}.{" "}
              <Btn variant="secondary" disabled={busy} onClick={() => revoke(d.mine)}>Revoke my login</Btn>
            </p>
          ) : (
            <p style={{ margin: "0 0 12px", fontSize: 14 }}>You have no login for this database yet.</p>
          )}
          <form onSubmit={create} style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <Field label="Valid for (days)" hint={`1 to ${d.max_valid_days}; ${d.default_valid_days} when empty.`}>
              <Input type="number" min={1} max={d.max_valid_days} value={days}
                     onChange={e => setDays(e.target.value)} style={{ width: 120 }} />
            </Field>
            <Btn type="submit" disabled={busy}>{d.mine ? "New password" : "Create my login"}</Btn>
          </form>
          {created && (
            <div role="status" style={{ marginTop: 16, padding: "12px 14px", borderRadius: 8,
                                        background: "rgba(0,150,64,0.06)", border: "1px solid rgba(0,150,64,0.35)" }}>
              <strong>Copy the password now — it is not shown again.</strong>
              <Snippet label="User">{created.login.role_name}</Snippet>
              <Snippet label="Password">{created.password}</Snippet>
              <Snippet label="Connection string">{created.connection_string}</Snippet>
              <Snippet label="psql">{created.psql}</Snippet>
              <Snippet label="R (DBI + RPostgres)">
                {`con <- DBI::dbConnect(RPostgres::Postgres(), host = "${c.host}", port = ${c.port},\n  dbname = "${c.database}", user = "${created.login.role_name}", password = "<the password>")`}
              </Snippet>
              <Snippet label="Python (psycopg)">
                {`conn = psycopg.connect("postgresql://${created.login.role_name}:<the password>@${c.host}:${c.port}/${c.database}")`}
              </Snippet>
            </div>
          )}
          {d.can_admin && d.all && d.all.length > 0 && (
            <table className="responsive-table" style={{ marginTop: 16, width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <caption style={{ textAlign: "left", fontWeight: 600, padding: "4px 0" }}>Logins of this database</caption>
              <thead>
                <tr style={{ textAlign: "left", color: "#6b7280" }}>
                  <th scope="col">Person</th><th scope="col">Login</th><th scope="col">Role</th>
                  <th scope="col">Expires</th><th scope="col"><span style={{ position: "absolute", left: -9999 }}>Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {d.all.map(l => (
                  <tr key={l.id} style={{ borderTop: "1px solid #e5e7eb" }}>
                    <td data-label="Person" style={{ padding: "4px 0" }}>{l.username}</td>
                    <td data-label="Login" style={MONO}>{l.role_name}</td>
                    <td data-label="Role">{l.level}</td>
                    <td data-label="Expires">{when(l.expires_at)}</td>
                    <td style={{ textAlign: "right" }}>
                      <Btn variant="secondary" disabled={busy} onClick={() => revoke(l)}>
                        Revoke<span style={{ position: "absolute", left: -9999 }}> the login of {l.username}</span>
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </section>
  );
}
